import { useAuth } from "../context/AuthContext";
import { apiFetch } from "../lib/api";
import { useState, useEffect } from "react";

type Props = {
  projectName: string;
};

/* ── Quick Launch apps ── */
const QUICK_LAUNCH = [
  { id: "chat", label: "Chat", icon: "M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" },
  { id: "files", label: "Files", icon: "M13 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V9z M13 2v7h7" },
  { id: "notebook", label: "Notebook", icon: "M4 19.5A2.5 2.5 0 016.5 17H20 M4 19.5A2.5 2.5 0 016.5 17H20V3H6.5A2.5 2.5 0 004 5.5v14z" },
  { id: "canvas", label: "Canvas", icon: "M12 19l7-7 3 3-7 7-3-3z M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z M2 2l7.586 7.586" },
  { id: "browser", label: "Browser", icon: "M12 2a10 10 0 100 20 10 10 0 000-20z M2 12h20 M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" },
  { id: "projects", label: "Projects", icon: "M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" },
];

/* ── Activity placeholder ── */
interface ActivityEntry {
  icon: string;
  message: string;
  time: string;
  color: string;
}

const PLACEHOLDER_ACTIVITY: ActivityEntry[] = [
  { icon: "✓", message: "Welcome to tool_os", time: "just now", color: "var(--ot-green, #34d399)" },
  { icon: "N", message: "Created note 'Architecture'", time: "8m ago", color: "var(--ot-purple)" },
];

/* ── Greeting helper ── */
function getGreeting(): string {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

export function HomeSurface({ projectName }: Props) {
  const [providerName, setProviderName] = useState("ollama");
  const [providerModel, setProviderModel] = useState("qwen2.5:7b");
  const [providerConnected, setProviderConnected] = useState(false);
  const [routingActive, setRoutingActive] = useState(false);
  const [windowCount, setWindowCount] = useState(0);
  const [uptime, setUptime] = useState("—");
  const [version] = useState(__GIT_SHA__);

  const [noteCount, setNoteCount] = useState(0);
  const [projectCount, setProjectCount] = useState(0);
  const [taskCount, setTaskCount] = useState(0);
  const [costStr, setCostStr] = useState("$0.00");

  const [activity, setActivity] = useState<ActivityEntry[]>(PLACEHOLDER_ACTIVITY);
  const [animatedIn, setAnimatedIn] = useState(false);
  const { user } = useAuth();
  const userName = user?.login || "there";

  useEffect(() => {
    setAnimatedIn(true);

    // Provider status
    apiFetch("/api/status")
      .then((r) => r.json())
      .then((d) => {
        if (d.provider) setProviderName(d.provider);
        if (d.model) setProviderModel(d.model);
        if (typeof d.connected === "boolean") setProviderConnected(d.connected);
        if (typeof d.routing_active === "boolean") setRoutingActive(d.routing_active);
        if (typeof d.window_count === "number") setWindowCount(d.window_count);
        if (d.uptime) setUptime(d.uptime);
      })
      .catch(() => {});

    // Cost
    apiFetch("/api/cost/summary")
      .then((r) => r.json())
      .then((d) => {
        if (d.total_24h !== undefined) setCostStr(`$${d.total_24h.toFixed(2)}`);
      })
      .catch(() => {});

    // Audit log for activity
    apiFetch("/api/admin/audit")
      .then((r) => r.json())
      .then((d) => {
        if (Array.isArray(d) && d.length > 0) {
          setActivity(
            d.slice(0, 8).map((e: Record<string, string>) => ({
              icon: (e.action?.[0] ?? "?").toUpperCase(),
              message: e.description ?? e.action ?? "Unknown action",
              time: e.timestamp ?? "",
              color: "var(--ot-accent)",
            })),
          );
        }
      })
      .catch(() => {});

    // Notes count from localStorage
    try {
      const notes = localStorage.getItem("opentool-notebook-entries");
      if (notes) {
        const parsed = JSON.parse(notes);
        setNoteCount(Array.isArray(parsed) ? parsed.length : 0);
      }
    } catch {}

    // Project / task counts from localStorage
    try {
      const projects = localStorage.getItem("opentool-projects");
      if (projects) {
        const parsed = JSON.parse(projects);
        setProjectCount(Array.isArray(parsed) ? parsed.length : 0);
      }
    } catch {}

    try {
      const tasks = localStorage.getItem("opentool-tasks");
      if (tasks) {
        const parsed = JSON.parse(tasks);
        setTaskCount(Array.isArray(parsed) ? parsed.length : 0);
      }
    } catch {}
  }, []);

  function navigate(route: string) {
    window.location.hash = route;
  }

  return (
    <div className={`home-page ${animatedIn ? "home-page--in" : ""}`}>
      {/* ── Section 1: Welcome Header ── */}
      <header className="home-welcome">
        <div className="home-welcome__left">
          <h1 className="home-welcome__wordmark"><span className="home-welcome__prompt">&gt;</span> tool<span className="home-welcome__os">_os</span></h1>
          <p className="home-welcome__subtitle">Your AI development workspace</p>
        </div>
        <div className="home-welcome__right">
          <span className="home-welcome__greeting">{getGreeting()}, {userName}</span>
          <span className="home-welcome__provider">
            <span className={`home-welcome__dot ${providerConnected ? "home-welcome__dot--on" : "home-welcome__dot--off"}`} />
            {providerName} — {providerConnected ? "connected" : "disconnected"}
          </span>
        </div>
      </header>

      {/* ── Section 2: Quick Launch ── */}
      <section className="home-launch">
        {QUICK_LAUNCH.map((app, i) => (
          <button
            key={app.id}
            className="home-launch__card"
            style={{ animationDelay: `${i * 0.06}s` }}
            onClick={() => navigate(app.id)}
          >
            <svg className="home-launch__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d={app.icon} />
            </svg>
            <span className="home-launch__label">{app.label}</span>
          </button>
        ))}
      </section>

      {/* ── Section 3 + 4: Activity + System Status ── */}
      <section className="home-mid">
        <div className="home-activity">
          <div className="home-activity__header">
            <span className="home-activity__title">Activity</span>
            <span className="home-activity__badge">{activity.length} events</span>
          </div>
          <div className="home-activity__list">
            {activity.map((entry, i) => (
              <div
                key={`${entry.message}-${i}`}
                className="home-activity__row"
                style={{ animationDelay: `${0.2 + i * 0.05}s` }}
              >
                <span className="home-activity__row-icon" style={{ borderColor: entry.color, color: entry.color }}>
                  {entry.icon}
                </span>
                <span className="home-activity__row-msg">{entry.message}</span>
                <span className="home-activity__row-time">{entry.time}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="home-status">
          <div className="home-status__header">
            <span className="home-status__title">System</span>
          </div>
          <div className="home-status__rows">
            <div className="home-status__row">
              <span className="home-status__row-label">Provider</span>
              <span className="home-status__row-value">
                {providerName} ({providerModel})
                <span className={`home-status__indicator ${providerConnected ? "home-status__indicator--on" : "home-status__indicator--off"}`} />
              </span>
            </div>
            <div className="home-status__row">
              <span className="home-status__row-label">Routing</span>
              <span className="home-status__row-value">
                {routingActive ? "active" : "inactive"}
                <span className={`home-status__indicator ${routingActive ? "home-status__indicator--on" : "home-status__indicator--off"}`} />
              </span>
            </div>
            <div className="home-status__row">
              <span className="home-status__row-label">Windows</span>
              <span className="home-status__row-value">{windowCount}</span>
            </div>
            <div className="home-status__row">
              <span className="home-status__row-label">Uptime</span>
              <span className="home-status__row-value">{uptime}</span>
            </div>
            <div className="home-status__row">
              <span className="home-status__row-label">Version</span>
              <span className="home-status__row-value">{version}</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Section 5: Workspace Stats ── */}
      <section className="home-stats">
        <div className="home-stats__card">
          <span className="home-stats__card-value" style={{ color: "var(--ot-accent)" }}>{noteCount}</span>
          <span className="home-stats__card-label">Notes</span>
        </div>
        <div className="home-stats__card">
          <span className="home-stats__card-value" style={{ color: "var(--ot-purple)" }}>{projectCount}</span>
          <span className="home-stats__card-label">Projects</span>
        </div>
        <div className="home-stats__card">
          <span className="home-stats__card-value" style={{ color: "var(--ot-cyan)" }}>{taskCount}</span>
          <span className="home-stats__card-label">Tasks</span>
        </div>
        <div className="home-stats__card">
          <span className="home-stats__card-value" style={{ color: "var(--ot-green)" }}>{costStr}</span>
          <span className="home-stats__card-label">Cost (24h)</span>
        </div>
      </section>
    </div>
  );
}
